// ─────────────────────────────────────────────────────────────────────────────
// ForceBalanceIndicator
// Live port-vs-starboard force balance bar for two-gate scullers.
// Only renders when fresh readings exist on BOTH sides — sweep boats and
// single_scull toggles return null. Designed to slot into WorkoutMonitorV2
// just above the main split display.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

const FRESH_MS = 5000; // readings older than this stop counting

const fmtN = (n) => {
  const v = Math.round(Number(n) || 0);
  return Number.isFinite(v) ? v : 0;
};

export default function ForceBalanceIndicator({ latestBySide, className = '' }) {
  // Tick once a second so stale readings drop out of the indicator.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const port = latestBySide?.port;
  const starboard = latestBySide?.starboard;

  const portFresh = port && (now - (port.timestamp || 0)) < FRESH_MS;
  const starboardFresh = starboard && (now - (starboard.timestamp || 0)) < FRESH_MS;

  if (!portFresh || !starboardFresh) return null;

  const portF = Math.max(0, port.force_n || 0);
  const starF = Math.max(0, starboard.force_n || 0);
  const total = portF + starF;
  if (total <= 0) return null;

  const portPct = (portF / total) * 100;
  const starPct = 100 - portPct;
  const offset = portPct - 50; // positive = port stronger
  const offsetAbs = Math.abs(offset);

  // Tone / label thresholds — under 3% is effectively balanced
  let label = 'Balanced';
  let labelTone = 'text-emerald-600 dark:text-emerald-400';
  let labelTestId = 'force-balance-status-balanced';
  if (offsetAbs >= 3) {
    const stronger = offset > 0 ? 'Bowside' : 'Strokeside';
    label = `${stronger} +${offsetAbs.toFixed(1)}%`;
    labelTone = offsetAbs >= 8
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-amber-600 dark:text-amber-400';
    labelTestId = `force-balance-status-${offset > 0 ? 'port' : 'starboard'}-stronger`;
  }

  return (
    <div
      className={`px-3 py-2 rounded-xl bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 shadow-sm ${className}`}
      data-testid="force-balance-indicator"
      role="group"
      aria-label="Bowside vs strokeside force balance"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Force Balance
          </span>
          <span
            className={`text-[11px] font-bold uppercase tracking-wider ${labelTone}`}
            data-testid={labelTestId}
          >
            {label}
          </span>
        </div>
        <div className="flex items-center gap-3 font-mono tabular-nums text-[11px] text-zinc-600 dark:text-zinc-300">
          <span data-testid="force-balance-port-newtons">{fmtN(portF)}N</span>
          <span className="text-zinc-300 dark:text-zinc-600">·</span>
          <span data-testid="force-balance-starboard-newtons">{fmtN(starF)}N</span>
        </div>
      </div>

      {/* Split bar */}
      <div
        className="relative h-2.5 w-full rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden flex"
        aria-hidden="true"
      >
        <div
          data-testid="force-balance-port-bar"
          className="h-full bg-rose-500 transition-[flex-basis] duration-500 ease-out"
          style={{ flexBasis: `${portPct}%` }}
        />
        <div
          data-testid="force-balance-starboard-bar"
          className="h-full bg-sky-500 transition-[flex-basis] duration-500 ease-out"
          style={{ flexBasis: `${starPct}%` }}
        />
        {/* Centre 50% reference line */}
        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/60 dark:bg-zinc-900/60" />
      </div>

      <div className="flex items-center justify-between mt-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
        <span>Bow · {portPct.toFixed(0)}%</span>
        <span>{starPct.toFixed(0)}% · Stroke</span>
      </div>
    </div>
  );
}
